<script setup>
import { computed, inject, onMounted } from 'vue'
import { Eye, EyeClosed, GripHorizontal, Trash, OctagonAlert, SquareStack } from 'lucide-vue-next'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@codinglabsau/gooey'

const tipTapNodeId = inject('elementId')
const form = inject('form')
const viewport = inject('viewport')
const selectedItem = inject('selectedItem')
const selectItem = inject('selectItem')
const deselectItem = inject('deselectItem')
const removeItem = inject('removeItem')

const props = defineProps({
  element: {
    type: Object,
    required: true,
  },
  group: {
    type: String,
    required: false,
    default: null,
  },
  parent: {
    type: Object,
    required: false,
    default: null,
  },
  section: {
    type: String,
    required: false,
    default: null,
  },
})

const hasError = computed(() => {
  return Object.keys(form.errors).some(key => key.includes(`content.${tipTapNodeId}.items.${props.element.id}`))
})

const childrenErrorKey = `content.${tipTapNodeId}.items.${props.element.id}.children`

const childrenError = computed(() => {

  if (form.errors[childrenErrorKey] !== undefined) {
    return form.errors[childrenErrorKey]
  }

  return null
})

const isSelected = computed(() => {
  return selectedItem.value?.id === props.element.id
})

const onRemove = () => {
  if (selectedItem?.value?.id === props.element.id) {
    deselectItem()
  }

  removeItem(props.element)
}
</script>

<template>
  <li
    role="option"
    @click.stop="selectItem(element)"
    class="bg-gray-50 cursor-pointer text-black"
    :class="{
          '!bg-gray-300': isSelected && element.viewport[viewport].visible === false,
          'bg-indigo-500 text-white': isSelected,
        }"
  >
    <div class="flex items-center p-3 gap-3">
      <div class="cursor-move">
        <GripHorizontal class="size-4" />
      </div>
      <div class="flex w-full items-center justify-between">
        <div class="flex items-center gap-1">
          <span class="capitalize">{{ element.name ? element.name : element.component }}</span>
          <OctagonAlert v-if="hasError" class="size-4" :class="isSelected ? 'text-white' : 'text-red-500' " />
          <p v-if="childrenError !== null" class="text-red-500 text-sm">{{ childrenError }}</p>
        </div>
        <div class="flex items-center gap-4 mr-2">
          <span class="text-sm">
            {{ element.component }}
          </span>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger
                @click.stop="element.viewport[viewport].visible = !element.viewport[viewport].visible">
                <Eye v-if="element.viewport[viewport].visible" class="size-4" />
                <EyeClosed v-else class="size-4" />
              </TooltipTrigger>
              <TooltipContent class="bg-black text-white py-1 px-2">
                {{ __(':state for :viewport', {
                viewport,
                state: element.viewport[viewport].visible ? 'Visible' : 'Hidden',
              }) }}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button type="button" @click.stop="onRemove">
                  <Trash class="size-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent class="bg-black text-white py-1 px-2">
                {{ __('Remove Item') }}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
    </div>
    <div class="ml-[1.2rem] border-l" v-if="element.hasChildren">
      <slot name="children" :errorKey="childrenErrorKey" />
    </div>
  </li>
</template>
